/* eslint-disable max-lines */
import React, {
 useEffect, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    channelService
} from 'shared/api';
import {
    Assistant, Opcode, OpcodeEntry, makeEmptyAssistant
} from '../types/builderAssistant.types';
import OpCodeSelectionModal from './OpCodeSelectionModal';
import InputMappingEditor from './InputMappingEditor';
import EntitySchemaBuilder from './EntitySchemaBuilder';
import MemoryConfigSection from './MemoryConfigSection';
import GovernanceSection from './GovernanceSection';
import ChipInput from './ChipInput';

interface AssistantFormProps {
  form: Partial<Assistant>;
  setForm: React.Dispatch<React.SetStateAction<Partial<Assistant>>>;
}

// Tabs-based full-form editor (used by the Update modal). Wraps the same
// section components the wizard uses so logic stays in one place.
export default function AssistantForm({
 form, setForm
}: AssistantFormProps) {
    const {
 oragID, subOragID
} = useParams<{ oragID: string; subOragID: string }>();
    const [availableOpcodes, setAvailableOpcodes] = useState<Opcode[]>([]);
    const [loading, setLoading] = useState(true);
    const [channels, setChannels] = useState<Array<{ _id: string; topic?: string; address?: string }>>([]);

    // Fill missing fields with defaults so deeply-nested accessors don't crash
    // when editing a record with an older shape.
    const f: Assistant = {
 ...makeEmptyAssistant(oragID || '', subOragID || ''),
...(form as Assistant)
};

    useEffect(() => { loadOpcodes(); loadChannels(); }, []);

    const loadOpcodes = async () => {
        try {
            setLoading(true);
            // A-0008.03: V1 ("Classic") removed; single source of truth.
            const resJson = await jsonOpcodeService.listJSONOpcodes({
                organizationId: oragID!,
                subOrganizationId: subOragID!,
            });
            const rawJson: any[] = Array.isArray(resJson?.data) ? resJson.data : [];
            const uJson = Array.from(new Map(rawJson.filter((o: any) => o._id || o.opcode_id || o.id).map((o: any) => [(o._id || o.opcode_id || o.id), o])).values());
            const jsonOps: Opcode[] = uJson.map((op: any) => ({
                ...op,
                name: op.title || op.name,
                opcode_id: op._id || op.opcode_id || op.id,
                inputs: op.inputs || op.definitions?.inputs || [],
            }));
            setAvailableOpcodes(jsonOps);
        } catch (e) {
            console.error('Failed to load opcodes', e);
        } finally {
            setLoading(false);
        }
    };

    const loadChannels = async () => {
        try {
            const res = await channelService.getChannels(oragID || '', subOragID || '');
            const data = (res as any)?.data || [];
            setChannels(Array.isArray(data) ? data : []);
        } catch (err) {
            console.warn('Failed to load channels', err);
        }
    };

    const update = (patch: Partial<Assistant>) => setForm(prev => ({
 ...prev,
...patch
}));

    const selectedIds = (f.opcodes || []).map(e => e.opcode_id);

    const handleOpcodesSelected = (ids: string[]) => {
        const byId = new Map((f.opcodes || []).map(e => [e.opcode_id, e] as const));
        const next: OpcodeEntry[] = ids.map(id => byId.get(id) || ({
            opcode_id: id,
purpose: '',
description: '',
input_mapping: {},
        }));
        update({
 opcodes: next
});
    };

    return (
        <Tabs defaultValue="identity" className="w-full">
            <TabsList className="flex flex-wrap h-auto">
                <TabsTrigger value="identity">Identity</TabsTrigger>
                <TabsTrigger value="opcodes">OpCodes</TabsTrigger>
                <TabsTrigger value="typed">Typed Input</TabsTrigger>
                <TabsTrigger value="knowledge">Knowledge</TabsTrigger>
                <TabsTrigger value="instructions">Instructions</TabsTrigger>
                <TabsTrigger value="channels">Channels</TabsTrigger>
                <TabsTrigger value="memory">Memory</TabsTrigger>
                <TabsTrigger value="governance">Governance</TabsTrigger>
            </TabsList>

            <TabsContent value="identity" className="space-y-3">
                <div>
                    <Label>Assistant Name <small className="text-red-500">*</small></Label>
                    <Input value={f.title} onChange={(e) => update({
 title: e.target.value
})} />
                </div>
                {f.assistant_id && (
                    <div>
                        <Label className="text-xs text-muted-foreground">Internal id</Label>
                        <div className="font-mono text-xs text-muted-foreground">{f.assistant_id}</div>
                    </div>
                )}
                <div>
                    <Label>Description</Label>
                    <Textarea value={f.description} onChange={(e) => update({
 description: e.target.value
})} rows={2} />
                </div>
                <div>
                    <Label>Persona</Label>
                    <Textarea value={f.persona} onChange={(e) => update({
 persona: e.target.value
})} rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label>Version</Label>
                        <Input value={f.version} onChange={(e) => update({
 version: e.target.value
})} />
                    </div>
                    <div>
                        <Label>Status</Label>
                        <Select value={f.status} onValueChange={(v) => update({
 status: v as Assistant['status']
})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="active">active</SelectItem>
                                <SelectItem value="inactive">inactive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <div><Label>Disabled</Label>
                        <div className="text-xs text-muted-foreground">Block all execute requests.</div>
                    </div>
                    <Switch checked={f.disabled} onCheckedChange={(v) => update({
 disabled: !!v
})} />
                </div>
            </TabsContent>

            <TabsContent value="opcodes" className="space-y-3">
                <div>
                    <Label>Selected opcodes</Label>
                    <OpCodeSelectionModal
                        disabled={loading}
                        opcodes={availableOpcodes}
                        defaultSelectedOpcodes={selectedIds}
                        multiSelect
                        onOpcodeSelect={(res: any) => handleOpcodesSelected(res as string[])}
                    />
                    <div className="flex flex-wrap gap-1 mt-2">
                        {selectedIds.map(id => {
                            const found = availableOpcodes.find(o => o.opcode_id === id);
                            return <Badge key={id} variant="secondary" className="text-xs">{found?.name || id}</Badge>;
                        })}
                    </div>
                </div>
                <div>
                    <Label>Default opcode</Label>
                    <Select
                        value={f.default_opcode || ''}
                        onValueChange={(v) => update({
 default_opcode: v
})}
                    >
                        <SelectTrigger><SelectValue placeholder="Pick from selected" /></SelectTrigger>
                        <SelectContent>
                            {selectedIds.map(id => {
                                const found = availableOpcodes.find(o => o.opcode_id === id);
                                return <SelectItem key={id} value={id}>{found?.name || id}</SelectItem>;
                            })}
                        </SelectContent>
                    </Select>
                </div>
                {(f.opcodes || []).length > 0 && (
                    <div className="space-y-2 pt-2 border-t">
                        <Label>Opcode metadata</Label>
                        {(f.opcodes || []).map(entry => {
                            const found = availableOpcodes.find(o => o.opcode_id === entry.opcode_id);
                            const inputs = found?.inputs || [];
                            return (
                                <div key={entry.opcode_id} className="border rounded-md p-2 space-y-2">
                                    <div className="font-medium text-sm">{found?.name || entry.opcode_id}</div>
                                    <Input
                                        value={entry.purpose}
                                        placeholder="purpose"
                                        className="text-xs"
                                        onChange={(e) => update({
                                            opcodes: (f.opcodes || []).map(ee =>
                                                ee.opcode_id === entry.opcode_id ? {
 ...ee,
purpose: e.target.value
} : ee
                                            ),
                                        })}
                                    />
                                    <Textarea
                                        value={entry.description}
                                        placeholder="description"
                                        rows={2}
                                        className="text-xs"
                                        onChange={(e) => update({
                                            opcodes: (f.opcodes || []).map(ee =>
                                                ee.opcode_id === entry.opcode_id ? {
 ...ee,
description: e.target.value
} : ee
                                            ),
                                        })}
                                    />
                                    <InputMappingEditor
                                        opcodeInputs={(inputs as any[]).map((i: any) => typeof i === 'string' ? i : i?.name).filter(Boolean)}
                                        typedInput={f.typed_input}
                                        value={entry.input_mapping}
                                        onChange={(next) => update({
                                            opcodes: (f.opcodes || []).map(ee =>
                                                ee.opcode_id === entry.opcode_id ? {
 ...ee,
input_mapping: next
} : ee
                                            ),
                                        })}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </TabsContent>

            <TabsContent value="typed" className="space-y-3">
                <div>
                    <Label>Context keys</Label>
                    <ChipInput
                        values={f.typed_input.context_keys}
                        onChange={(v) => update({
 typed_input: {
 ...f.typed_input,
context_keys: v
}
})}
                    />
                </div>
                <div>
                    <Label>Propensity keys</Label>
                    <ChipInput
                        values={f.typed_input.propensity_keys}
                        onChange={(v) => update({
 typed_input: {
 ...f.typed_input,
propensity_keys: v
}
})}
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label>Intent format</Label>
                        <Select
                            value={f.typed_input.intent.format}
                            onValueChange={(v) => update({
                                typed_input: {
 ...f.typed_input,
intent: {
 ...f.typed_input.intent,
format: v as 'text' | 'structured'
}
},
                            })}
                        >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="text">text</SelectItem>
                                <SelectItem value="structured">structured</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Routing mode</Label>
                        <Select
                            value={f.typed_input.intent.routing_mode}
                            onValueChange={(v) => update({
                                typed_input: {
 ...f.typed_input,
intent: {
 ...f.typed_input.intent,
routing_mode: v as 'intent_match' | 'default_only'
}
},
                            })}
                        >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="default_only">default_only</SelectItem>
                                <SelectItem value="intent_match">intent_match</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <EntitySchemaBuilder
                    value={f.typed_input.entity_schema}
                    onChange={(v) => update({
 typed_input: {
 ...f.typed_input,
entity_schema: v
}
})}
                />
            </TabsContent>

            <TabsContent value="knowledge" className="space-y-3">
                <Label>Knowledge collections</Label>
                <RichRowEditor
                    value={f.knowledge as unknown as Array<Record<string, string>>}
                    onChange={(v) => update({
                        knowledge: v as unknown as Assistant['knowledge'],
                    })}
                    idField="collection_id"
                    idPlaceholder="kb-collection-id"
                    addLabel="Add collection"
                />
                <Label>Data connections (APIs)</Label>
                <RichRowEditor
                    value={f.data_connections as unknown as Array<Record<string, string>>}
                    onChange={(v) => update({
                        data_connections: v as unknown as Assistant['data_connections'],
                    })}
                    idField="connection_id"
                    idPlaceholder="api-connection-id"
                    addLabel="Add connection"
                />
            </TabsContent>

            <TabsContent value="instructions" className="space-y-3">
                <div>
                    <Label>System prompt</Label>
                    <Textarea
                        value={f.instructions.system_prompt || ''}
                        onChange={(e) => update({
 instructions: {
 ...f.instructions,
system_prompt: e.target.value || null
}
})}
                        rows={4}
                    />
                </div>
                <div>
                    <Label>Guidelines</Label>
                    <Textarea
                        value={f.instructions.guidelines || ''}
                        onChange={(e) => update({
 instructions: {
 ...f.instructions,
guidelines: e.target.value || null
}
})}
                        rows={3}
                    />
                </div>
                <div>
                    <Label>Content policy</Label>
                    <Textarea
                        value={f.instructions.content_policy || ''}
                        onChange={(e) => update({
 instructions: {
 ...f.instructions,
content_policy: e.target.value || null
}
})}
                        rows={3}
                    />
                </div>
            </TabsContent>

            <TabsContent value="channels" className="space-y-3">
                <Label>Mode</Label>
                <Select
                    value={f.channels.mode}
                    onValueChange={(v) => update({
 channels: {
 ...f.channels,
mode: v as 'restricted' | 'open'
}
})}
                >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="restricted">restricted</SelectItem>
                        <SelectItem value="open">open</SelectItem>
                    </SelectContent>
                </Select>
                <Label>{f.channels.mode === 'restricted' ? 'Authorized channels' : 'Channels (informational)'}</Label>
                <div className="space-y-2">
                    <div className="flex flex-wrap gap-1">
                        {f.channels.authorized_channels.map(id => (
                            <Badge key={id} variant="secondary" className="text-xs gap-1">
                                {channels.find(c => c._id === id)?.topic || id}
                                <button type="button" onClick={() => update({
                                    channels: {
 ...f.channels,
authorized_channels: f.channels.authorized_channels.filter(c => c !== id)
},
                                })}>×</button>
                            </Badge>
                        ))}
                    </div>
                    <div className="text-xs text-muted-foreground">{channels.length} channel(s) available on this sub-org.</div>
                    <div className="space-y-1 max-h-32 overflow-auto border rounded p-2">
                        {channels.map(ch => {
                            const on = f.channels.authorized_channels.includes(ch._id);
                            return (
                                <label key={ch._id} className="flex items-center gap-2 text-xs cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={on}
                                        onChange={() => update({
                                            channels: {
                                                ...f.channels,
                                                authorized_channels: on
                                                    ? f.channels.authorized_channels.filter(c => c !== ch._id)
                                                    : [...f.channels.authorized_channels, ch._id],
                                            },
                                        })}
                                    />
                                    <span className="font-mono">{ch._id}</span>
                                    <span className="text-muted-foreground">— {ch.topic || ch.address || ''}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
                {f.channels.mode === 'restricted' && f.channels.authorized_channels.length === 0 && (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-200 p-2 rounded">
                        Warning: restricted + empty denies every request.
                    </div>
                )}
            </TabsContent>

            <TabsContent value="memory">
                <MemoryConfigSection
                    value={f.memory}
                    onChange={(v) => update({
 memory: v
})}
                />
            </TabsContent>

            <TabsContent value="governance">
                <GovernanceSection
                    value={f.governance}
                    onChange={(v) => update({
 governance: v
})}
                />
            </TabsContent>
        </Tabs>
    );
}

// Small row editor used for both knowledge[] and data_connections[] in the
// update form (keeps identical UX to the wizard's version).
interface RichRowEditorProps<T extends Record<string, string>> {
  value: T[];
  onChange: (v: T[]) => void;
  idField: keyof T;
  idPlaceholder: string;
  addLabel: string;
}

function RichRowEditor<T extends Record<string, string>>({
 value, onChange, idField, idPlaceholder, addLabel
}: RichRowEditorProps<T>) {
    const update = (i: number, key: keyof T, v: string) => {
        const next = [...value];
        next[i] = {
 ...next[i],
[key]: v
};
        onChange(next);
    };
    return (
        <div className="space-y-2">
            {(value || []).map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                    <Input
                        value={row[idField] as string}
                        onChange={(e) => update(i, idField, e.target.value)}
                        placeholder={idPlaceholder}
                        className="text-xs flex-1"
                    />
                    <Input
                        value={(row as any).description || ''}
                        onChange={(e) => update(i, 'description' as keyof T, e.target.value)}
                        placeholder="description"
                        className="text-xs flex-[2]"
                    />
                    <button className="text-sm" onClick={() => onChange(value.filter((_, idx) => idx !== i))}>×</button>
                </div>
            ))}
            <button
                type="button"
                className="text-xs underline"
                onClick={() => {
                    const empty = {
 [idField]: '',
description: ''
} as unknown as T;
                    onChange([...(value || []), empty]);
                }}
            >
                + {addLabel}
            </button>
        </div>
    );
}
